import React, { useRef, useState } from 'react';

import { createStyles, InputLabel, Theme, withStyles, WithStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import { noop } from 'lodash';

import fileUploadsResource from '@lib/resources/fileUploadsResource';
import configService from '@lib/services/configService';
import storageService from '@lib/services/storageService';

import DialogImageEnhancer from 'components/DialogImageEnhancer';
import FileUpload from 'components/FileUpload';

import { AvatarProps } from './Avatar';
import Typography from './Typography';

const styles = (theme: Theme) => {
  const {
    custom: {
      palette: { colorGrayishBlue },
    },
    palette: {
      common: { white },
    },
    spacing: { unit },
  } = theme;

  return createStyles({
    root: {
      width: 200,
      height: 'fit-content',
    },
    avatar: {
      backgroundColor: colorGrayishBlue,
      fontSize: '1.5em',
      height: 46,
      position: 'relative',
      width: 46,
      '& img': {
        borderRadius: 'inherit',
      },
    },
    avatarButton: {
      border: `1px solid ${colorGrayishBlue}`,
      marginTop: unit * 2,
      width: 106,
      overflow: 'hidden !important',
      padding: unit / 2,
      '&:hover $avatarOverlay': {
        opacity: 1,
      },
    },
    avatarOverlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      bottom: 0,
      height: '100%',
      opacity: 0,
      paddingTop: unit * 3 + 4,
      position: 'absolute',
      textAlign: 'center',
      transition: '.5s ease',
      width: '100%',
    },
    avatarOverlayIcon: {
      color: white,
      fontSize: 20,
    },
    avatarOverlayText: {
      color: white,
      fontSize: 9,
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    removePhotoButton: {
      marginTop: unit * 2,
    },
    removePhotoButtonIcon: {
      fontSize: 17,
      marginRight: unit / 2,
    },
    removePhotoButtonLabel: {
      textTransform: 'capitalize',
    },
    uploadFileLink: {
      marginLeft: theme.spacing.unit * 2,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    label: {
      fontSize: 11,
      marginBottom: theme.spacing.unit,
      marginLeft: theme.spacing.unit,
    },
    avatarWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
  });
};

export type UploaderAvatarData = { name: string; extension: string; fileBase64: string };

export interface UploaderAvatarProps extends WithStyles<typeof styles>, Omit<AvatarProps, 'classes'> {
  avatarId?: string;
  label?: string;
  backgroundColor?: string;
  fallback?: React.ReactNode;
  disabled?: boolean;
  onUpload: (data: UploaderAvatarData) => void;
}

const UploaderAvatar: React.FC<UploaderAvatarProps> = ({
  classes,
  avatarId,
  children,
  backgroundColor,
  fallback,
  label,
  disabled,
  onUpload,
}) => {
  const fileUploadRef = useRef({ open: noop });
  const [dialogImageEnhancerOpen, setDialogImageEnhancerOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>();

  const initialSrc = avatarId
    ? `${configService.getApiBasePath()}${fileUploadsResource.url}/${avatarId}/file?token=${storageService.getToken()}`
    : '';
  const [src, setSrc] = useState(initialSrc);

  const handleDialogImageEnhancer = async (base64: string) => {
    if (base64) {
      setDialogImageEnhancerOpen(false);
      setSrc(base64);
      onUpload({
        name: uploadedFile.name,
        extension: uploadedFile.extension,
        fileBase64: base64,
      });
    } else {
      setDialogImageEnhancerOpen(false);
    }
  };

  const handleFileUpload = (file: any) => {
    setDialogImageEnhancerOpen(true);
    setUploadedFile(file);
  };

  const handleUploadPhotoButtonClick = () => {
    fileUploadRef.current.open();
  };

  return (
    <>
      <div className={classes.root}>
        {label && <InputLabel className={classes.label}>{label}</InputLabel>}
        <div className={classes.avatarWrapper}>
          {src || !fallback ? (
            <Avatar
              className={classes.avatar}
              src={src}
              style={{ backgroundColor: src ? 'transparent' : backgroundColor }}
            >
              {children}
            </Avatar>
          ) : (
            fallback
          )}
          {!disabled && (
            <Typography
              variant="body1Bold"
              className={classes.uploadFileLink}
              role="button"
              onClick={handleUploadPhotoButtonClick}
            >
              Upload image
            </Typography>
          )}
        </div>
      </div>
      <FileUpload
        accept={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
        errorMessage="Sorry, you can only upload image files."
        fileUploadRef={fileUploadRef}
        onFileSubmit={handleFileUpload}
      />
      <DialogImageEnhancer
        open={dialogImageEnhancerOpen || false}
        source={uploadedFile ? uploadedFile.file.preview : ''}
        onRequestClose={handleDialogImageEnhancer}
      />
    </>
  );
};

export default withStyles(styles)(UploaderAvatar);
